import { Box, Chip, Collapse, Grid, Typography } from '@mui/material';
import React, { useState } from 'react';
import ShowMoreButton from 'src/App/Components/Buttons/ShowMoreButton';
import { BarDataCard } from 'src/App/HOC';
import {
  COLOR_INFO,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1_300,
  COLOR_SECONDARY_1_600,
  COLOR_TERTIARY_1,
  COLOR_TERTIARY_1_500,
  COLOR_TERTIARY_1_600,
  COLOR_TERTIARY_1_700,
  COLOR_TERTIARY_1_800,
  GRAPH_BLUE,
} from 'src/colors';
import { amenitiesObject } from 'src/const';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { BarGraphData } from 'src/interfaces/idashboard';

const DEMO_DATA: BarGraphData[] = [
  { keyShort: 'Parking', keyLong: 'Parking', value: 3.45 },
  { keyShort: 'Gym', keyLong: 'Gym', value: 27.42 },
  { keyShort: 'Dryer', keyLong: 'Dryer', value: 7.69 },
  { keyShort: 'Washer', keyLong: 'Washer', value: 4.19 },
  { keyShort: 'AC', keyLong: 'Air Conditioning', value: 6.24 },
  { keyShort: 'WiFi', keyLong: 'WiFi', value: 7.92 },
  { keyShort: 'TV', keyLong: 'Television', value: 10 },
  { keyShort: 'BBQ Grill', keyLong: 'BBQ Grill', value: 2.43 },
  { keyShort: 'Fireplace', keyLong: 'Fireplace', value: 1.73 },
  { keyShort: 'Breakfast', keyLong: 'Breakfast', value: 8.91 },
  { keyShort: 'Amazing Views', keyLong: 'Amazing Views', value: 7.74 },
];

const DEMO_LOW_YIELD_AMINITY = ['Hot Tub', 'Pets', 'Pool', 'Kitchen'];

const AmenitiesImpactOnRevenueChart = ({
  revenueImpactOnAmenitiesDistro,
  isDisabled,
  userTailorMarketAccess,
  handleOnSampleClick,
}) => {
  const [showAmenitiesWithoutNoticibleRevenue, setShowAmenitiesWithoutNoticibleRevenue] = useState<
    boolean
  >(false);
  const upgradeMessage = useUpgradeMesageProFeature();
  const processAmenityChartData = () => {
    let amenityChartData: BarGraphData[] = [];
    const amenitiesWithoutNoticibleRevenue: string[] = [];

    for (const amenity in revenueImpactOnAmenitiesDistro) {
      const amenityData: BarGraphData = {
        keyShort: amenitiesObject[amenity],
        keyLong: amenitiesObject[amenity],
        value: revenueImpactOnAmenitiesDistro[amenity],
      };
      amenityChartData.push(amenityData);
    }

    amenityChartData = amenityChartData.filter(amenityGraphObject => {
      if (amenityGraphObject.value < 5) {
        amenitiesWithoutNoticibleRevenue.push(amenityGraphObject.keyLong);
      }
      return amenityGraphObject.value >= 5;
    });

    amenityChartData.sort((a, b) => b.value - a.value);
    return { amenityChartData, amenitiesWithoutNoticibleRevenue };
  };

  const { amenityChartData, amenitiesWithoutNoticibleRevenue } = isDisabled
    ? { amenityChartData: DEMO_DATA, amenitiesWithoutNoticibleRevenue: DEMO_LOW_YIELD_AMINITY }
    : processAmenityChartData();

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      spacing={1}
      sx={{ padding: '5px', boxShadow: '0 4px 10px #0003', borderRadius: 1, position: 'relative' }}
    >
      <Grid container xs={12} sx={{ padding: '20px' }}>
        <Grid item xs={12}>
          <BarDataCard
            title="Revenue by amenities"
            data={amenityChartData}
            orientation="horizontal"
            unit="%"
            label="%"
            graphHeight={180}
            barColor={GRAPH_BLUE}
            barAccentColor={COLOR_TERTIARY_1_800}
            tooltipTextSuffix={' increase in revenue'}
          />
        </Grid>

        <Grid container xs={12} sx={{marginBottom: showAmenitiesWithoutNoticibleRevenue? '0' : '-16px'}}>
          <ShowMoreButton
            isShowingLess={!showAmenitiesWithoutNoticibleRevenue}
            handleButtonClick={() => setShowAmenitiesWithoutNoticibleRevenue(prev => !prev)}
            additionalStyle={{marginRight: '-16px'}}
          />
        </Grid>

        <Collapse in={showAmenitiesWithoutNoticibleRevenue}>
          <Grid item xs={12} sx={{ mt: '10px', mb: '10px' }}>
            <Typography variant="body1">Amenities without noticeable Revenue impact</Typography>
          </Grid>
          <Grid item xs={12} sx={{ mb: '10px' }}>
            {amenitiesWithoutNoticibleRevenue &&
              amenitiesWithoutNoticibleRevenue.length > 0 &&
              amenitiesWithoutNoticibleRevenue.map(amenitiy => {
                return (
                  <Chip
                    label={amenitiy}
                    variant={'outlined'}
                    sx={{
                      minWidth: 50,
                      marginTop: '10px',
                      marginRight: '5px',
                      borderColor: COLOR_PRIMARY_1,
                      borderWidth: '1px',
                      color: COLOR_PRIMARY_1,
                      fontWeight: 500,
                      '&.MuiChip-filled': {
                        backgroundColor: COLOR_PRIMARY_1,
                        color: '#fff',
                      },
                      // ...styles,
                    }}
                  />
                );
              })}
          </Grid>
        </Collapse>
      </Grid>
      {isDisabled && (
        <Typography
          variant="body1"
          sx={{
            position: 'absolute',
            top: '55px',
            left: 0,
            width: '100%',
            height: 'calc(100% - 60px)',
            fontSize: '20px',
            fontWeight: 500,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
            background: '#fff5',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => {
            handleOnSampleClick(!userTailorMarketAccess ? 'expert' : 'pro');
          }}
        >
          {upgradeMessage}
        </Typography>
      )}
    </Grid>
  );
};

export default AmenitiesImpactOnRevenueChart;
